// Inline conditions summary displayed above the product grid search bar.
// Shows each condition as a compact row with status and criteria.

import {
  computeOrderConditionStatus,
  computeProductConditionStatuses,
  conditionsUseProductMetadata,
  type SelectionRuleCondition,
  type SelectionRule as BaseSelectionRule,
} from "@/lib/conditions";
import {
  calculateCartQuantity,
  calculateCartTotal,
  exceedsMaxPrice,
  exceedsMaxQuantity,
  formatPrice,
  meetsMinQuantity,
  type CartItem,
} from "@/lib/queries/products";
import { Badge } from "@repo/ui";
import { CheckCircle2, DollarSign, Info, Package } from "lucide-react";
import { StatusBadge } from "./conditions-dialog";

interface SelectionRule extends BaseSelectionRule {
  label?: string | null;
  description?: string | null;
}

interface ConditionsSummaryProps {
  selectionRule: SelectionRule;
  cart: CartItem[];
  currencyCode?: string;
  showPrice?: boolean;
  isShopifyType?: boolean;
  onConditionClick?: (index: number) => void;
  activeConditionIndex?: number | null;
}

export function ConditionsSummary({
  selectionRule,
  cart,
  currencyCode = "USD",
  showPrice = true,
  isShopifyType = false,
  onConditionClick,
  activeConditionIndex,
}: ConditionsSummaryProps) {
  const hasConditions =
    selectionRule.conditions && selectionRule.conditions.length > 0;
  const hasGlobalRules =
    selectionRule.minQuantity != null ||
    selectionRule.maxQuantity != null ||
    (showPrice && selectionRule.maxPrice != null);

  if (!hasConditions && !hasGlobalRules) {
    return null;
  }

  const orderStatus = computeOrderConditionStatus(cart, selectionRule);
  const conditionStatuses = computeProductConditionStatuses(
    cart,
    selectionRule.conditions ?? [],
  );
  const cartQty = calculateCartQuantity(cart);
  const cartTotal = calculateCartTotal(cart);

  return (
    <div className="space-y-3">
      {/* All-met banner */}
      {orderStatus.fulfilled && (
        <div className="flex items-center gap-2 p-2.5 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
          <p className="text-sm font-medium text-green-700">
            All conditions met
          </p>
        </div>
      )}

      {/* Shopify metadata warning */}
      {isShopifyType &&
        conditionsUseProductMetadata(selectionRule.conditions ?? []) && (
          <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
            <Info className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-600">
              Some conditions use metadata not available for Shopify products.
            </p>
          </div>
        )}

      {/* Order-level rules */}
      {hasGlobalRules && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {selectionRule.minQuantity != null && (
            <OrderRuleChip
              icon={<Package className="h-3 w-3" />}
              fulfilled={meetsMinQuantity(cart, selectionRule.minQuantity)}
              hasCart={cart.length > 0}
            >
              Min {selectionRule.minQuantity} items
              {cart.length > 0 && (
                <span className="text-muted-foreground/70"> ({cartQty})</span>
              )}
            </OrderRuleChip>
          )}
          {selectionRule.maxQuantity != null && (
            <OrderRuleChip
              icon={<Package className="h-3 w-3" />}
              fulfilled={!exceedsMaxQuantity(cart, selectionRule.maxQuantity)}
              hasCart={cart.length > 0}
            >
              Max {selectionRule.maxQuantity} items
              {cart.length > 0 && (
                <span className="text-muted-foreground/70"> ({cartQty})</span>
              )}
            </OrderRuleChip>
          )}
          {showPrice && selectionRule.maxPrice != null && (
            <OrderRuleChip
              icon={<DollarSign className="h-3 w-3" />}
              fulfilled={!exceedsMaxPrice(cart, selectionRule.maxPrice)}
              hasCart={cart.length > 0}
            >
              Max {formatPrice(selectionRule.maxPrice, currencyCode)}
              {cart.length > 0 && (
                <span className="text-muted-foreground/70">
                  {" "}
                  ({formatPrice(cartTotal, currencyCode)})
                </span>
              )}
            </OrderRuleChip>
          )}
        </div>
      )}

      {/* Product conditions */}
      {hasConditions && (
        <div className="space-y-2.5">
          {selectionRule.conditions?.map((condition, index) => {
            const status = conditionStatuses[index];
            return (
              <ConditionRow
                key={condition.id ?? `cond-${index}`}
                condition={condition}
                currencyCode={currencyCode}
                showPrice={showPrice}
                matchingQuantity={status?.matchingQuantity ?? 0}
                fulfilled={status?.fulfilled ?? false}
                hasCart={cart.length > 0}
                onClick={
                  onConditionClick ? () => onConditionClick(index) : undefined
                }
                isActive={activeConditionIndex === index}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function OrderRuleChip({
  icon,
  fulfilled,
  hasCart,
  children,
}: {
  icon: React.ReactNode;
  fulfilled: boolean;
  hasCart: boolean;
  children: React.ReactNode;
}) {
  const colorClass =
    hasCart && fulfilled
      ? "bg-green-50 text-green-700 border-green-200"
      : hasCart && !fulfilled
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-muted text-muted-foreground border-border";

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs ${colorClass}`}
    >
      {icon}
      {children}
    </span>
  );
}

function ConditionRow({
  condition,
  currencyCode,
  showPrice,
  matchingQuantity,
  fulfilled,
  hasCart,
  onClick,
  isActive,
}: {
  condition: SelectionRuleCondition;
  currencyCode: string;
  showPrice: boolean;
  matchingQuantity: number;
  fulfilled: boolean;
  hasCart: boolean;
  onClick?: () => void;
  isActive?: boolean;
}) {
  const quantityLabel =
    condition.quantityType === "MINIMUM"
      ? "min"
      : condition.quantityType === "EXACT"
        ? "exact"
        : "max";

  const borderClass = isActive
    ? "ring-2 ring-primary border-primary bg-primary/5"
    : hasCart
      ? fulfilled
        ? "border-green-200 bg-green-50/50"
        : condition.isRequired
          ? "border-amber-200 bg-amber-50/50"
          : "border-border"
      : "border-border";

  const clickableClass = onClick
    ? "cursor-pointer hover:shadow-sm transition-shadow"
    : "";

  // Collect criteria labels
  const criteria: string[] = [];
  if (condition.collections && condition.collections.length > 0) {
    criteria.push(
      `Collections: ${condition.collections.map((c) => c.name).join(", ")}`,
    );
  }
  if (condition.categories && condition.categories.length > 0) {
    criteria.push(`Categories: ${condition.categories.join(", ")}`);
  }
  if (condition.productTypes && condition.productTypes.length > 0) {
    criteria.push(`Types: ${condition.productTypes.join(", ")}`);
  }
  if (condition.vendors && condition.vendors.length > 0) {
    criteria.push(`Vendors: ${condition.vendors.join(", ")}`);
  }
  if (condition.tags && condition.tags.length > 0) {
    criteria.push(`Tags: ${condition.tags.join(", ")}`);
  }
  if (condition.products && condition.products.length > 0) {
    criteria.push(
      `Products: ${condition.products.map((p) => p.name).join(", ")}`,
    );
  }
  if (showPrice && (condition.priceMin != null || condition.priceMax != null)) {
    const min =
      condition.priceMin != null
        ? formatPrice(condition.priceMin, currencyCode)
        : "Any";
    const max =
      condition.priceMax != null
        ? formatPrice(condition.priceMax, currencyCode)
        : "Any";
    criteria.push(`Price: ${min} - ${max}`);
  }

  return (
    <div
      className={`rounded-lg border p-2.5 ${borderClass} ${clickableClass}`}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                if (e.key === " ") e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <h5 className="text-xs font-medium truncate">
            {condition.conditionName}
          </h5>
          <Badge
            variant={
              condition.isRequired && fulfilled
                ? "secondary"
                : condition.isRequired
                  ? "destructive"
                  : "secondary"
            }
            className={`text-[10px] px-1 py-0 shrink-0 ${condition.isRequired && fulfilled ? "bg-green-100 text-green-700 border-green-200" : ""}`}
          >
            {condition.isRequired ? "Required" : "Optional"}
          </Badge>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-[10px] text-muted-foreground">
            {matchingQuantity}/{condition.quantity} ({quantityLabel})
          </span>
          <StatusBadge fulfilled={fulfilled} hasCart={hasCart} />
          {isActive && (
            <span className="text-[10px] text-primary font-medium">✕</span>
          )}
        </div>
      </div>
      {criteria.length > 0 && (
        <p className="text-[10px] text-muted-foreground mt-1 line-clamp-2">
          {criteria.join(" · ")}
        </p>
      )}
    </div>
  );
}
